import type React from 'react';
import type { ReactNode } from 'react';
import styled, { useTheme } from 'styled-components';

import type { LessonSectionFormat, Video, SectionExerciseGroupTemplate } from 'app/types/graphql';
import type { HandleSectionFocusChangeProps } from 'app/pages/workout';

import Typography from 'app/ui/components/atoms/typography';
import ExerciseItem from 'app/ui/components/molecules/exercise-item';

import { ExercisePickerProvider } from 'app/ui/components/molecules/picker/exercise/util';

type BaseContentData = {
  title: string;
  content?: string | ReactNode | null;
  extraContent?: string | null;
  additionalInfo: string;
  id: string;
};

export type ContentItemData = BaseContentData & {
  originalExerciseId?: string;
  video?: Video | null;
  notes?: string | null;
  alternativeExercises?: ContentItemData[] | null;
  rpe?: number | null;
};

export type GroupContentItemData = BaseContentData & {
  sectionExercises: ContentItemData[];
  showTitle: boolean;
  notes?: string | null;
  nestedGroups?: GroupContentItemData[];
  template?: SectionExerciseGroupTemplate | null;
};

export type ContentData = BaseContentData & {
  titleExtraContent: ReactNode;
  contentItems?: ReactNode;
  notes?: string | null;
  groups?: GroupContentItemData[] | null;
  format?: LessonSectionFormat;
  duration?: number;
  workTime?: number | null;
  restTime?: number | null;
  rounds?: number | null;
  expandTextByDefault?: boolean;
};

export type ContentItemsAreaProps = {
  items: GroupContentItemData[];
  contentItemFocused: string | null;
  handleContentItemClick: (prop: HandleSectionFocusChangeProps) => void;
  hideExerciseChevrons?: boolean;
};

const Title = styled(Typography)`
  margin: ${({ theme: { spacing } }) => `${spacing(4)} 0 ${spacing(2)} 0`};
  font-weight: ${({ theme }) => theme.typography.bold};
`;

const ContentItemArea = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const ContentItem = styled.div`
  width: 100%;
`;

const GroupDescription = styled.div`
  margin-bottom: ${({ theme: { spacing } }) => `${spacing(2)}`};
`;

const GroupRenderer: React.FC<{
  group: GroupContentItemData;
  contentItemFocused: string | null;
  handleContentItemClick: (prop: HandleSectionFocusChangeProps) => void;
  isNested?: boolean;
  hideExerciseChevrons?: boolean;
}> = ({ group, contentItemFocused, handleContentItemClick, hideExerciseChevrons = false }) => {
  const theme = useTheme();
  const { id: groupId, title, content, showTitle, sectionExercises, nestedGroups } = group;

  const directExercises = nestedGroups?.length
    ? sectionExercises.filter(
        (exercise) =>
          !nestedGroups.some((nestedGroup) =>
            nestedGroup.sectionExercises.some((nestedEx) => nestedEx.id === exercise.id),
          ),
      )
    : sectionExercises;

  return (
    <ContentItemArea key={`group-${groupId}`}>
      <ContentItem>
        {showTitle && (
          <>
            <Title variant="body2" color={theme.colors.paragraphGray}>
              {title}
            </Title>
            {content && typeof content === 'string' && content.trim().length > 0 && (
              <GroupDescription>
                <Typography variant="caption" color={theme.colors.paragraphGray}>
                  {content}
                </Typography>
              </GroupDescription>
            )}
          </>
        )}
        {directExercises?.length > 0 && (
          <div>
            {directExercises.map((sectionExercise, index) => (
              <ExerciseItem
                key={sectionExercise.id}
                isOpen={contentItemFocused === sectionExercise.id}
                groupId={groupId}
                initialExercise={sectionExercise}
                onClick={handleContentItemClick}
                index={index}
                hideChevrons={hideExerciseChevrons}
              />
            ))}
          </div>
        )}
        {nestedGroups?.map((nestedGroup) => (
          <GroupRenderer
            key={`nested-${nestedGroup.id}`}
            group={nestedGroup}
            contentItemFocused={contentItemFocused}
            handleContentItemClick={handleContentItemClick}
            hideExerciseChevrons={hideExerciseChevrons}
          />
        ))}
      </ContentItem>
    </ContentItemArea>
  );
};

const ContentItemAreasWrapper: React.FC<ContentItemsAreaProps> = ({
  items,
  contentItemFocused,
  handleContentItemClick,
  hideExerciseChevrons = false,
}) => {
  if (!items.length) {
    return <></>;
  }

  return (
    <ExercisePickerProvider>
      {items.map((item) => (
        <GroupRenderer
          key={`group-${item.id}`}
          group={item}
          contentItemFocused={contentItemFocused}
          handleContentItemClick={handleContentItemClick}
          hideExerciseChevrons={hideExerciseChevrons}
        />
      ))}
    </ExercisePickerProvider>
  );
};

export default ContentItemAreasWrapper;
